/**
 * Cognito Hosted-UI OAuth — Authorization Code + PKCE — for the N.E.S.T. SPA.
 *
 * WHY this exists (single-login harmonisation):
 * The SPA used to sign in with SRP (lib/cognito.ts), which establishes a token
 * set but NOT a Cognito hosted-UI session. The internal assets (/ops behind ALB
 * authenticate-cognito, /analytics behind CF Access + Cognito IdP) authenticate
 * via the hosted UI. With SRP, a user who signed into the SPA would be prompted
 * AGAIN when hitting those assets — the double login we're eliminating.
 *
 * By signing the SPA in through the SAME hosted UI (this module), one Cognito
 * login establishes the shared session; the ALB/CF gates then SSO silently — one
 * prompt, everywhere.
 *
 * What stays identical: the ID token (carrying `cognito:groups` + `email`) is
 * still what the nest-api fetch layer reads, and the `nest-access` group gate in
 * authStore is unchanged. Only the *acquisition* of the token changes.
 *
 * Config (set per environment, so this is pool-agnostic):
 *   VITE_COGNITO_DOMAIN   — hosted-UI domain, e.g.
 *                           "phenom-dev-hasura-auth.auth.us-east-1.amazoncognito.com"
 *   VITE_COGNITO_CLIENT_ID — public app client (no secret; PKCE)
 * Redirect URI is derived from the current origin so dev/preview/prod each use
 * their own callback without extra env.
 */

const DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined
const SCOPES = 'openid email profile'

const STORAGE = {
  idToken: 'nest.oauth.id_token',
  accessToken: 'nest.oauth.access_token',
  refreshToken: 'nest.oauth.refresh_token',
  expiresAt: 'nest.oauth.expires_at', // epoch ms
  pkceVerifier: 'nest.oauth.pkce_verifier',
  state: 'nest.oauth.state', // CSRF/fixation nonce for the authorize round-trip (#451)
  returnTo: 'nest.oauth.return_to',
} as const

/**
 * Clamp a post-login destination to a same-origin RELATIVE path (#451). Blocks
 * open-redirect: rejects absolute URLs, protocol-relative `//evil`, and the
 * `/\evil` backslash trick (which several browsers normalise to `//`). Anything
 * that isn't a plain `/path` falls back to `/`.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string {
  if (!raw || !/^\/(?![/\\])/.test(raw)) return '/'
  return raw
}

export interface OAuthTokens {
  idToken: string
  accessToken: string
  /** Absent on a refresh-token grant (Cognito reuses the existing refresh token). */
  refreshToken?: string
  expiresAt: number
}

function assertConfigured(): { domain: string; clientId: string } {
  if (!DOMAIN || !CLIENT_ID) {
    throw new Error(
      'Missing VITE_COGNITO_DOMAIN or VITE_COGNITO_CLIENT_ID env vars',
    )
  }
  return { domain: DOMAIN, clientId: CLIENT_ID }
}

function redirectUri(): string {
  // Registered as a callback on the Cognito app client (see infra).
  return `${window.location.origin}/auth/callback`
}

// ---- PKCE helpers -----------------------------------------------------------

function base64UrlEncode(bytes: ArrayBuffer): string {
  const arr = new Uint8Array(bytes)
  let str = ''
  for (const b of arr) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomVerifier(): string {
  const bytes = new Uint8Array(64)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes.buffer)
}

async function challengeFromVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  )
  return base64UrlEncode(digest)
}

// ---- Flow -------------------------------------------------------------------

/**
 * Kick off interactive login: build a PKCE challenge, stash the verifier, and
 * redirect to the Cognito hosted UI. When the user already has a hosted-UI
 * session (e.g. established by the edge gate), Cognito returns to /auth/callback
 * without showing a prompt — that's the silent SSO that makes login single.
 *
 * `returnTo` is preserved so the callback can send the user back where they were.
 */
export async function beginLogin(
  returnTo: string = '/',
  identityProvider?: string,
): Promise<void> {
  const { domain, clientId } = assertConfigured()
  const verifier = randomVerifier()
  const challenge = await challengeFromVerifier(verifier)
  // CSRF/fixation nonce: bound to this browser, echoed back on /auth/callback
  // and verified there. PKCE protects the code exchange; `state` protects
  // against login-CSRF / code-injection landing on a victim. (#451)
  const state = randomVerifier()
  sessionStorage.setItem(STORAGE.pkceVerifier, verifier)
  sessionStorage.setItem(STORAGE.state, state)
  sessionStorage.setItem(STORAGE.returnTo, sanitizeReturnTo(returnTo))

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri(),
    scope: SCOPES,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  })
  // When a federated provider is named (Google / SignInWithApple), pin the
  // authorize request to it. Cognito then skips its own selection UI and bounces
  // straight to the IdP, so the user never sees the hosted login page — they go
  // from our terminal directly to the provider, then back to /auth/callback.
  if (identityProvider) {
    params.set('identity_provider', identityProvider)
  }
  window.location.assign(`https://${domain}/oauth2/authorize?${params}`)
}

/**
 * Exchange the authorization code (from the /auth/callback redirect) for tokens,
 * using the stashed PKCE verifier. Persists tokens and returns them.
 */
export async function exchangeCodeForTokens(code: string): Promise<OAuthTokens> {
  const { domain, clientId } = assertConfigured()
  const verifier = sessionStorage.getItem(STORAGE.pkceVerifier)
  if (!verifier) throw new Error('Missing PKCE verifier; restart sign-in.')

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  })
  const resp = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!resp.ok) {
    throw new Error(`Token exchange failed: ${resp.status} ${await resp.text()}`)
  }
  const json = await resp.json()
  sessionStorage.removeItem(STORAGE.pkceVerifier)
  return persistTokens(json)
}

/**
 * Refresh the ID/access tokens from the stored refresh token. Returns null when
 * there's no refresh token or the grant is rejected (caller treats as logged-out).
 */
export async function refreshTokens(): Promise<OAuthTokens | null> {
  const { domain, clientId } = assertConfigured()
  const refreshToken = localStorage.getItem(STORAGE.refreshToken)
  if (!refreshToken) return null

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  })
  const resp = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!resp.ok) return null
  const json = await resp.json()
  // Cognito does not return a new refresh_token on refresh; keep the existing one.
  return persistTokens({ ...json, refresh_token: refreshToken })
}

/**
 * Persist tokens obtained OUTSIDE the hosted-UI code exchange (in-app
 * email/password via amazon-cognito-identity-js, or a direct USER_AUTH /
 * WebAuthn InitiateAuth response) into the SAME `nest.oauth.*` storage shape the
 * OAuth flow uses. This is what lets `getStoredTokens`, `isExpired`, and
 * `refreshTokens` work identically regardless of how the session was minted, so
 * restoreSession + the silent refresh loop need zero special-casing.
 *
 * `expiresAt` (the absolute epoch-ms used internally) is derived from Cognito's
 * ABSOLUTE epoch-SECONDS token expiry (getAccessToken().getExpiration()). When
 * omitted we fall back to now + one hour, the Cognito default token lifetime.
 */
export function persistSessionTokens(input: {
  idToken: string
  accessToken: string
  refreshToken?: string
  /** ABSOLUTE epoch SECONDS (Cognito's getExpiration() unit). Optional. */
  expiresAtSeconds?: number
}): OAuthTokens {
  const nowMs = Date.now()
  const expiresAt =
    typeof input.expiresAtSeconds === 'number' && input.expiresAtSeconds > 0
      ? input.expiresAtSeconds * 1000
      : nowMs + 3600_000
  // persistTokens stores a relative `expires_in`; convert our absolute target
  // back to a (clamped, never negative) offset so the on-disk shape is identical
  // to the OAuth-code path.
  const expiresInSeconds = Math.max(1, Math.round((expiresAt - nowMs) / 1000))
  return persistTokens({
    id_token: input.idToken,
    access_token: input.accessToken,
    refresh_token: input.refreshToken,
    expires_in: expiresInSeconds,
  })
}

export function getStoredTokens(): OAuthTokens | null {
  const idToken = localStorage.getItem(STORAGE.idToken)
  const accessToken = localStorage.getItem(STORAGE.accessToken)
  const expiresAt = Number(localStorage.getItem(STORAGE.expiresAt) ?? 0)
  if (!idToken || !accessToken) return null
  return {
    idToken,
    accessToken,
    refreshToken: localStorage.getItem(STORAGE.refreshToken) ?? undefined,
    expiresAt,
  }
}

/** True when there's no token or it expires within the next 60s. */
export function isExpired(tokens: OAuthTokens | null): boolean {
  if (!tokens) return true
  return Date.now() > tokens.expiresAt - 60_000
}

export function clearTokens(): void {
  for (const k of Object.values(STORAGE)) localStorage.removeItem(k)
  sessionStorage.removeItem(STORAGE.pkceVerifier)
  sessionStorage.removeItem(STORAGE.state)
  sessionStorage.removeItem(STORAGE.returnTo)
}

/**
 * Read and clear the stored OAuth `state` nonce. The callback compares the
 * `state` echoed by Cognito to this value; a missing or mismatched state means
 * the callback wasn't initiated by this browser and MUST be rejected. (#451)
 */
export function consumeState(): string | null {
  const s = sessionStorage.getItem(STORAGE.state)
  sessionStorage.removeItem(STORAGE.state)
  return s
}

/** Redirect to the hosted-UI logout (clears the shared Cognito session too). */
/**
 * Sign out of N.E.S.T. and land on the sign-in page (phenom-backend#709).
 *
 * Sign-in is in-app (password or passkey; federated sign-in is off since #701),
 * so there is no hosted-UI session to end and no reason to bounce through
 * Cognito's /logout, which also rejected dev-nest as a logout_uri. Instead:
 * clear the local tokens, revoke the refresh token with Cognito (best effort,
 * keepalive so it survives the navigation), and go to /sign-in.
 */
export function signOut(): void {
  const refreshToken = localStorage.getItem(STORAGE.refreshToken)
  clearTokens()
  if (refreshToken) revokeRefreshToken(refreshToken)
  window.location.assign('/sign-in')
}

function revokeRefreshToken(token: string): void {
  const poolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined
  if (!poolId || !CLIENT_ID) return
  try {
    void fetch(`https://cognito-idp.${poolId.split('_')[0]}.amazonaws.com/`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.RevokeToken',
      },
      body: JSON.stringify({ Token: token, ClientId: CLIENT_ID }),
    }).catch(() => {
      // Revocation is best effort; the tokens are already gone from this device.
    })
  } catch {
    // fetch unavailable: same as above.
  }
}

export function consumeReturnTo(): string {
  const to = sessionStorage.getItem(STORAGE.returnTo) ?? '/'
  sessionStorage.removeItem(STORAGE.returnTo)
  // Defence-in-depth: re-clamp on the way out so a tampered sessionStorage value
  // can't drive an off-origin redirect at the callback. (#451)
  return sanitizeReturnTo(to)
}

/**
 * Decode a JWT's payload (no signature verification — the token came straight
 * from Cognito's token endpoint over TLS). Used to read `cognito:groups` +
 * `email` for the nest-access gate, mirroring the old SDK's decodePayload().
 */
export function decodeIdToken(idToken: string): Record<string, unknown> {
  const part = idToken.split('.')[1]
  if (!part) throw new Error('Malformed ID token')
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
  return JSON.parse(new TextDecoder().decode(bytes))
}

// ---- internal ---------------------------------------------------------------

interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string
  expires_in: number // seconds
}

function persistTokens(json: TokenResponse): OAuthTokens {
  const expiresAt = Date.now() + json.expires_in * 1000
  localStorage.setItem(STORAGE.idToken, json.id_token)
  localStorage.setItem(STORAGE.accessToken, json.access_token)
  if (json.refresh_token) {
    localStorage.setItem(STORAGE.refreshToken, json.refresh_token)
  }
  localStorage.setItem(STORAGE.expiresAt, String(expiresAt))
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt,
  }
}
